import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { unsubscribeFromPush } from '@services/pushNotifications'

// Types
export type ContractType = 'PLATFORM_AGREEMENT'
export type ContractStatus = 'DRAFT' | 'SENT' | 'CUSTOMER_SIGNED' | 'SIGNED' | 'CANCELLED' | 'TERMINATED' | 'EXPIRED'

export interface UserContract {
  type: ContractType
  status: ContractStatus
  expires_at: string | null
  // Optional enriched fields returned by /api/auth/me (may not always be present)
  id?: number
  contract_number?: string | null
  signed_at?: string | null
  // V3 Platform Agreement: roles within the contract
  roles?: string[]
}

export interface User {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  role: 'admin' | 'tenant_admin' | 'sales' | 'investor' | 'signer'
  is_active: boolean
  created_at: string
  updated_at: string | null
  lex_koller_status?: string | null
  /** GwG identification status of the linked CRM contact (STORY-990) */
  kyc_status?: string | null
  preferred_language?: string | null
  function?: string | null
  direct_phone?: string | null
  design_version?: string | null
  /** STORY-1033: Active Mandant persisted server-side (users.active_company_id) */
  active_company_id?: number | null
  /**
   * STORY-1145: Whether a password is set at all. Accounts created via
   * onboarding, magic link or mobile signing have none — they set one via the
   * e-mail link instead of "change password". Absent (older API) → treat as true.
   */
  has_password?: boolean
}

// STORY-014: Company role info
export interface UserCompanyRole {
  id: number  // company_contact.id
  company_id: number
  company_name: string
  company_uid: string
  function: string | null
  signature_type: string | null
  is_internal: boolean
}

interface AuthState {
  // State
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  // STORY-014: Companies the user has access to
  companies: UserCompanyRole[]
  // STORY-014b: Multi-select company filter
  activeCompanies: UserCompanyRole[]
  // STORY-132: Investor contracts
  contracts: UserContract[]

  // Actions
  setAuth: (user: User, accessToken: string, refreshToken: string, companies?: UserCompanyRole[], contracts?: UserContract[]) => void
  setUser: (user: User) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  setCompanies: (companies: UserCompanyRole[]) => void
  setActiveCompanies: (companies: UserCompanyRole[]) => void
  /** STORY-1033: Adopt the Mandant the server confirmed as active */
  setActiveCompany: (company: UserCompanyRole) => void
  toggleCompany: (company: UserCompanyRole) => void
  selectAllCompanies: () => void
  deselectAllCompanies: () => void
  setContracts: (contracts: UserContract[]) => void
  logout: () => void
  setLoading: (loading: boolean) => void
}

const isInternalRole = (role?: string): boolean =>
  role === 'admin' || role === 'tenant_admin' || role === 'sales'

/**
 * STORY-1033: The Mandant the server resolves the tenant from wins over any
 * local pre-selection, so Store und users.active_company_id stay in sync.
 */
const serverActiveCompany = (
  companies: UserCompanyRole[],
  activeCompanyId?: number | null
): UserCompanyRole | undefined =>
  activeCompanyId == null
    ? undefined
    : companies.find((c) => c.company_id === activeCompanyId)

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      companies: [],
      activeCompanies: [],
      contracts: [],

      // Actions
      setAuth: (user, accessToken, refreshToken, companies = [], contracts = []) => {
        const current = get().activeCompanies
        const fromServer = serverActiveCompany(companies, user.active_company_id)
        let activeCompanies: UserCompanyRole[]

        if (fromServer) {
          // Server already knows the active Mandant — adopt it
          activeCompanies = [fromServer]
        } else if (isInternalRole(user.role)) {
          // Internal users: auto-select first company (single-select mode)
          activeCompanies = companies.length > 0 ? [companies[0]] : []
        } else if (companies.length === 1) {
          // External user with single company: auto-select
          activeCompanies = [companies[0]]
        } else if (current.length > 0) {
          // Preserve existing selection
          activeCompanies = current
        } else {
          activeCompanies = []
        }

        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          companies,
          activeCompanies,
          contracts,
        })
      },

      setUser: (user) =>
        set({
          user,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          accessToken,
          refreshToken,
        }),

      setCompanies: (companies) => {
        const { user, activeCompanies: current } = get()
        const fromServer = serverActiveCompany(companies, user?.active_company_id)
        let activeCompanies: UserCompanyRole[]

        if (fromServer) {
          activeCompanies = [fromServer]
        } else if (isInternalRole(user?.role)) {
          // Single-select mode: auto-select first company
          activeCompanies = companies.length > 0 ? [companies[0]] : []
        } else if (companies.length === 1) {
          activeCompanies = [companies[0]]
        } else if (current.length > 0) {
          activeCompanies = current
        } else {
          activeCompanies = []
        }

        set({ companies, activeCompanies })
      },

      setActiveCompanies: (companies) =>
        set({
          activeCompanies: companies,
        }),

      setActiveCompany: (company) => {
        const { user } = get()
        set({
          activeCompanies: [company],
          // Mirror users.active_company_id so a reload keeps the same Mandant
          user: user ? { ...user, active_company_id: company.company_id } : user,
        })
      },

      toggleCompany: (company) => {
        const { activeCompanies } = get()
        const exists = activeCompanies.some((c) => c.company_id === company.company_id)
        if (exists) {
          set({ activeCompanies: activeCompanies.filter((c) => c.company_id !== company.company_id) })
        } else {
          set({ activeCompanies: [...activeCompanies, company] })
        }
      },

      selectAllCompanies: () => {
        set({ activeCompanies: [...get().companies] })
      },

      deselectAllCompanies: () => {
        set({ activeCompanies: [] })
      },

      setContracts: (contracts) =>
        set({ contracts }),

      logout: () => {
        unsubscribeFromPush()
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          companies: [],
          activeCompanies: [],
          contracts: [],
        })
      },

      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),
    }),
    {
      name: 'immotron-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        companies: state.companies,
        activeCompanies: state.activeCompanies,
        contracts: state.contracts,
      }),
      // STORY-110: Migrate legacy states on rehydrate
      onRehydrateStorage: () => (state) => {
        if (!state) return
        const raw = localStorage.getItem('immotron-auth')
        if (!raw) return
        try {
          const parsed = JSON.parse(raw)
          const persisted = parsed?.state
          if (!persisted) return
          // Migrate old single activeCompany to array
          if (persisted.activeCompany && (!persisted.activeCompanies || persisted.activeCompanies.length === 0)) {
            state.activeCompanies = [persisted.activeCompany]
          }
          // STORY-110: Trim "Alle Firmen" legacy state to first company only
          if (state.activeCompanies.length > 1) {
            state.activeCompanies = [state.activeCompanies[0]]
          }
        } catch {
          // ignore parse errors
        }
      },
    }
  )
)

// Helper functions
export const getAccessToken = () => useAuthStore.getState().accessToken
export const getRefreshToken = () => useAuthStore.getState().refreshToken
export const isAuthenticated = () => useAuthStore.getState().isAuthenticated
export const getCurrentUser = () => useAuthStore.getState().user
export const getCompanies = () => useAuthStore.getState().companies
// STORY-014b: Backward-compat helper (returns first active company or null)
export const getActiveCompany = () => useAuthStore.getState().activeCompanies[0] ?? null
// Semantic alias: active Mandant/Tenant (same as getActiveCompany, clearer intent)
export const getActiveTenant = () => useAuthStore.getState().activeCompanies[0] ?? null
export const getActiveTenantId = (): number | undefined =>
  useAuthStore.getState().activeCompanies[0]?.company_id
export const getActiveCompanies = () => useAuthStore.getState().activeCompanies
export const getActiveCompanyIds = (): number[] =>
  useAuthStore.getState().activeCompanies.map((c) => c.company_id)
export const isInternalUser = (): boolean =>
  isInternalRole(useAuthStore.getState().user?.role)
// STORY-110: Always returns active company IDs for filtering (single-select, no "all")
export const getCompanyFilter = (): number[] | undefined => {
  const { activeCompanies } = useAuthStore.getState()
  if (activeCompanies.length === 0) {
    return undefined
  }
  return activeCompanies.map((c) => c.company_id)
}
// Internal companies helpers (for company-scoped entities)
export const getInternalCompanies = (): UserCompanyRole[] =>
  useAuthStore.getState().companies.filter((c) => c.is_internal)
export const getInternalCompanyId = (): number | undefined =>
  useAuthStore.getState().companies.find((c) => c.is_internal)?.company_id

// STORY-132: Contract helpers
const ACTIVE_STATUSES: ContractStatus[] = ['SIGNED']

const isContractCurrentlyActive = (contract: UserContract): boolean => {
  if (!ACTIVE_STATUSES.includes(contract.status)) return false
  if (contract.expires_at === null) return true
  return new Date(contract.expires_at) > new Date()
}

export const hasActiveContract = (type: ContractType): boolean =>
  useAuthStore.getState().contracts.some((c) => isContractCurrentlyActive(c) && c.type === type)

/**
 * STORY-1170: The v3 platform agreement is one contract type for buyers and
 * sellers alike — the side is recorded in `roles` (`BUYER` / `SELLER`, upper
 * case, straight from the API). Contracts without `roles` (data from before v3)
 * count as neither role.
 */
export const hasActiveContractRole = (type: ContractType, role: string): boolean =>
  useAuthStore.getState().contracts.some(
    (c) => isContractCurrentlyActive(c) && c.type === type && (c.roles ?? []).includes(role)
  )

export const isContractExpired = (type: ContractType): boolean =>
  useAuthStore.getState().contracts.some(
    (c) => c.type === type && !isContractCurrentlyActive(c)
  )

export const hasAnyActiveContract = (): boolean =>
  useAuthStore.getState().contracts.some(isContractCurrentlyActive)
